import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import { EmployerPurchaseProductType } from '../../../common/interfaces/entities';
import { CreditProductPurchaseOrderForAfterPayment } from './types';

const channelSuffix = '_CREDIT_PRODUCT_PURCHASE_COMPLETE';
const processingChannelName = 'CREDIT_PRODUCT_PURCHASE_PROCESSING';

function makeChannelName(
  tabId: string | null,
  productType: EmployerPurchaseProductType,
  suffix: string
) {
  const defaultTabId = 'default-tab';

  return `${tabId || defaultTabId}_${productType}${suffix}`;
}

function makeProcessingChannelName(tabId: string | null) {
  const defaultTabId = 'default-tab';

  return `${tabId || defaultTabId}_${processingChannelName}`;
}

const sessionStorageKey = 'purchasedProductBroadcastTabId';
function setPurchasedProductBroadcastTabId() {
  const id = window?.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : Date.now().toString();
  window.sessionStorage.setItem(sessionStorageKey, id);
}

setPurchasedProductBroadcastTabId();

export type CreditProductPurchaseProcessingPayload = {
  orderNumber: string;
  productType: EmployerPurchaseProductType;
  jobId?: string;
};

export const broadcastCreditProductPurchaseComplete = (
  creditProductPurchaseOrder: CreditProductPurchaseOrderForAfterPayment
) => {
  const tabId = window.sessionStorage.getItem(sessionStorageKey);
  const channelName = makeChannelName(
    tabId,
    creditProductPurchaseOrder.product.type,
    channelSuffix
  );
  const channel = new BroadcastChannel(channelName);
  channel.postMessage(creditProductPurchaseOrder);
  channel.close();
};

export const broadcastCreditProductPurchaseProcessing = (
  payload: CreditProductPurchaseProcessingPayload
) => {
  const tabId = window.sessionStorage.getItem(sessionStorageKey);
  const channelName = makeProcessingChannelName(tabId);
  const channel = new BroadcastChannel(channelName);
  channel.postMessage(payload);
  channel.close();
};

type DataCallback<T = CreditProductPurchaseOrderForAfterPayment> = (
  value: T
) => void;

export const useListenCreditProductPurchaseComplete = (
  listenableProductType: EmployerPurchaseProductType,
  onCreditProductPurchaseComplete: DataCallback
) => {
  const callbackRef = useRef(onCreditProductPurchaseComplete);
  callbackRef.current = onCreditProductPurchaseComplete;

  useEffect(() => {
    const channelName = makeChannelName(
      window.sessionStorage.getItem(sessionStorageKey),
      listenableProductType,
      channelSuffix
    );

    const channel =
      new BroadcastChannel<CreditProductPurchaseOrderForAfterPayment>(
        channelName
      );

    channel.addEventListener('message', callbackRef.current);
    return () => {
      channel.removeEventListener('message', callbackRef.current);
      channel.close();
    };
  }, [listenableProductType]);

  return null;
};

export const useListenCreditProductPurchaseProcessing = (
  onCreditProductPurchaseProcessing: DataCallback<CreditProductPurchaseProcessingPayload>
) => {
  const callbackRef = useRef(onCreditProductPurchaseProcessing);
  callbackRef.current = onCreditProductPurchaseProcessing;

  useEffect(() => {
    const channelName = makeProcessingChannelName(
      window.sessionStorage.getItem(sessionStorageKey)
    );

    const channel =
      new BroadcastChannel<CreditProductPurchaseProcessingPayload>(channelName);

    channel.addEventListener('message', callbackRef.current);
    return () => {
      channel.removeEventListener('message', callbackRef.current);
      channel.close();
    };
  }, []);

  return null;
};
